
import React from 'react';
import {map, isEmpty} from 'lodash';
import {CHART_NAME, PERCENTAGE_ABSOLUTE_TABS_VALUE} from '../../../../utils/constants.js';
import {DateUtil} from '../../../../utils/date-util.js';

import RadioButtons from '../../../../ui-components/radio-buttons/radio-buttons.jsx';
import {COLOR_QUALITATIVE_CATEGORICAL} from '../../../../ui-components/chart-wrapper/chart-colors.js';
import {displayFromToDate} from '../charts-util.js';

export const MIN_CHANGE_KEY = 'minChange';
export const AVG_CHANGE_KEY = 'avgChange';
export const MAX_CHANGE_KEY = 'maxChange';
export const MIN_CHANGE_PERCENT_KEY = 'minChangePercent';
export const AVG_CHANGE_PERCENT_KEY = 'avgChangePercent';
export const MAX_CHANGE_PERCENT_KEY = 'maxChangePercent';

export const FARE_CHANGES_TYPES = {
    [MIN_CHANGE_KEY]: 'Lowest Fare Change',
    [AVG_CHANGE_KEY]: 'Average Fare Change',
    [MAX_CHANGE_KEY]: 'Highest Fare Change'
};

export const getFareChangeDetailsForOnDsAndODParisConfig = ({
                                                                isOnD,
                                                                origin,
                                                                computedData,
                                                                absoluteRelativeOnClickHandler,
                                                                handleBarChartOnClick,
                                                                relativeAbsolute,
                                                                showFaresBy,
                                                                filterBy
                                                            }) => {
    const fromTo = displayFromToDate(filterBy, DateUtil.DATE_FORMATS.MONTH_DAY_YEAR, true);

    return {
        chartHeight: 350,
        chartTitle: isOnD ? CHART_NAME.CHART_NUMBER_OF_O_AND_D_CHANGED_FARE : `${FARE_CHANGES_TYPES[showFaresBy]} FOR ORIGIN ${origin}`,
        chartDescription: isOnD ? `Shows number of O&Ds that changed fare for the origins selected [${fromTo}]` : `Shows the ${FARE_CHANGES_TYPES[showFaresBy]} of a competitor (across all RBD) [${fromTo}]`,
        configBars: {
            plotBars: computedData && computedData.plotBars || [],
            barSize: 20,
            isStackedBars: true,
            stackedValues: computedData && computedData.stackedValues || [],
            barOnClickHandler: handleBarChartOnClick
        },
        configXAxisGroup: {
            xGroupAxisKey: 'label',
            xGroupAxisHeight: isOnD ? 80 : 120,
            xGroupAxisTickMargin: 20,
            // xGroupAxisTickFormatter: tick => tick,
            // xGroupAxisTick: tick => tick
        },
        configYAxis: {
            yAxisTickFormatter: tick => tick
        },
        configToggle: {
            className: 'spark-pull-right',
            toggleName: isOnD ? 'absolute-relative-ond-fare-change' : 'absolute-relative-fare-change',
            selectedValue: relativeAbsolute,
            toggle: [
                {toggleLabel: 'Absolute', toggleValue: PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE},
                {toggleLabel: 'Relative', toggleValue: PERCENTAGE_ABSOLUTE_TABS_VALUE.RELATIVE}
            ],
            toggleTabOnClickHandler: absoluteRelativeOnClickHandler
        },
        configLegends: {
            isToggleChartOnClickOfLegend: true,
            isGroupLegend: true
        },
        isVisibleHorizontalGrid: true,
        isBrushVisible: true
    }
};

export const getFareChangeForMinAvgMaxConfig = ({
                                                    plotLines,
                                                    orgDest,
                                                    absoluteRelativeChangeFareForOrgDestTabsOnClickHandler,
                                                    changeFareForOrgDestCabinOnClickHandler,
                                                    relativeAbsolute,
                                                    cabinCodes = null,
                                                    cabinCode = null,
                                                    filterBy
                                                }) => {

    const fromTo = displayFromToDate(filterBy, DateUtil.DATE_FORMATS.MONTH_DAY_YEAR, true);

    const radioButtonsDetails = cabinCodes ? map(cabinCodes, cabinCode => { return {radioButtonLabel: cabinCode, radioButtonValue: cabinCode}; }) : [];

    return {
        chartHeight: 400,
        chartTitle: `Fare Change For ${orgDest}`,
        chartDescription: `Shows average, lowest and highest fare change (across all RBD) [${fromTo}]`,
        chartDenotation: <div className='col-xs-12 spark-mar-t-1 spark-bold'>
            <span className='chart-description--note dashed-line'>Dashed line:</span>&nbsp;<span>Lowest fare change</span>&nbsp;|&nbsp;
            <span className='chart-description--note thin-line'>Thin line:</span>&nbsp;<span>Average fare change</span>&nbsp;|&nbsp;
            <span className='chart-description--note thick-line'>Thick line:</span>&nbsp;<span>Highest fare change</span>
        </div>,
        configLines: {
            plotLines: getLineConfig(plotLines),
            lineWidth: 1,
            isDashedLine: false,
            strokeDashArray: '10 10'
        },
        configXAxis: {
            xAxisKey: 'dateValue',
            xAxisTickFormatter: tick => DateUtil.formatDate(tick, DateUtil.DATE_FORMATS.MONTH_NAME_DAY),
            xAxisPaddingLeft: 30,
            xAxisPaddingRight: 30
        },
        configYAxis: {
            yAxisTickFormatter: tick => tick
        },
        configToggle: {
            className: 'spark-pull-right',
            toggleName: 'absolute-relative-structural',
            selectedValue: relativeAbsolute,
            toggle: [
                {toggleLabel: 'Absolute', toggleValue: PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE},
                {toggleLabel: 'Relative', toggleValue: PERCENTAGE_ABSOLUTE_TABS_VALUE.RELATIVE}
            ],
            toggleTabOnClickHandler: absoluteRelativeChangeFareForOrgDestTabsOnClickHandler
        },
        chartFilters: <RadioButtons radioButtonsConfig={{
            radioButtonName: `CABIN-CODE-FARE-CHANGE-FOR-${orgDest}`,
            radioButtonsClassName: '',
            title: 'Cabin',
            radioButtonClassName: 'spark-mar-l-1 spark-bold',
            radioButtons: radioButtonsDetails,
            selectedValue: isEmpty(cabinCode) ? (cabinCodes && cabinCodes[0]) : cabinCode,
            isHorizontal: true,
            onChangeHandler: changeFareForOrgDestCabinOnClickHandler
        }}/>,
        configLegends: {
            isToggleChartOnClickOfLegend: true,
            isGroupLegend: true,
        }
    };
}

const getLineConfig = plotLines => {
    const lineDetails = [];
    map(plotLines, (eachCarrier, i) =>
        map(FARE_CHANGES_TYPES, (eachType, eachKey) => {
            lineDetails.push({
                lineKey: `${eachCarrier}_${eachKey}`,
                groupKey: eachCarrier,
                lineColor: COLOR_QUALITATIVE_CATEGORICAL.default[i],
                lineWidth: eachKey === MAX_CHANGE_KEY ? 4 : 1,
                isDashedLine: eachKey === MIN_CHANGE_KEY,
                lineType: 'linear'
            });
        })
    );

    return lineDetails;
}
